import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FileText,
  Share2,
  Calendar,
  Coffee,
  MessageCircle,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Pencil,
  Trash2,
  Timer,
  Plus,
  Crown,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useChat } from '../../contexts/ChatContext';
import { useNotes } from '../../contexts/NotesContext';
import { useBreakReminder } from '../../contexts/BreakReminderContext';
import { useCalendar } from '../../contexts/CalendarContext';
import PlusPromoModal from '../License/PlusPromoModal';
import ManageCategoriesModal from '../Notes/ManageCategoriesModal';
import ContextMenu from '../Common/ContextMenu';
import DeleteModal from '../Common/DeleteModal';
import categoriesApi from '../../services/categoriesApi';
import { getCategoryIcon } from '../../utils/categoryIcons';
import i18n from '../../i18n';

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const getNextBreakInfo = (breaks) => {
  if (!breaks || breaks.length === 0) return null;

  const now = new Date();
  const todayIdx = now.getDay(); // 0=sun

  let best = null; // { name, secondsUntil }

  breaks.forEach((b) => {
    if (!b.startTime) return;
    const [hStr, mStr] = b.startTime.split(':');
    const bh = parseInt(hStr, 10);
    const bm = parseInt(mStr, 10);
    if (isNaN(bh) || isNaN(bm)) return;

    // Check the next 7 days (including today)
    for (let offset = 0; offset < 7; offset++) {
      const dayIdx = (todayIdx + offset) % 7;
      const dayKey = DAY_KEYS[dayIdx];
      if (!b.activeDays?.[dayKey]) continue;

      const target = new Date(now);
      target.setDate(now.getDate() + offset);
      target.setHours(bh, bm, 0, 0);

      const diff = Math.floor((target - now) / 1000);
      if (diff <= 0) continue; // already passed

      if (best === null || diff < best.secondsUntil) {
        best = { name: b.name || i18n.t('shared:navSidebar.defaultBreakName'), secondsUntil: diff };
      }
      break; // found the nearest occurrence for this break
    }
  });

  return best;
};

const formatCountdown = (secs) => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const NavSidebar = ({ activeSection, onSectionChange, isCollapsed = false, onToggle }) => {
  const { t } = useTranslation('shared');
  const NAV_ITEMS = [
    { id: 'calendar', label: t('navSidebar.navItems.calendar'), icon: Calendar, path: '/calendar' },
    { id: 'break-reminder', label: t('navSidebar.navItems.breakReminder'), icon: Coffee, path: '/break-reminder' },
    { id: 'chat', label: t('navSidebar.navItems.chat'), icon: MessageCircle, path: '/chat' },
  ];
  const navigate = useNavigate();
  const location = useLocation();
  const { isPlusUser, authToken } = useAuth();
  const { theme } = useTheme();
  const isElectron = !!window.api;
  const { hasUnreadMessages } = useChat();
  const { notes, updateCategoryInNotes } = useNotes();
  const { breaks } = useBreakReminder();
  const { calendars, events, getEventsForDate } = useCalendar();

  const todayEvents = useMemo(() => {
    const today = new Date();
    const allTodayEvents = [];
    const seenIds = new Set();
    calendars.forEach(calendar => {
      const dayEvents = getEventsForDate(today, calendar.id);
      dayEvents.forEach(event => {
        if (seenIds.has(event.id)) return;
        seenIds.add(event.id);
        allTodayEvents.push({ ...event, calendar });
      });
    });
    return allTodayEvents.sort((a, b) => {
      if (a.allDay && !b.allDay) return -1;
      if (!a.allDay && b.allDay) return 1;
      if (a.allDay && b.allDay) return 0;
      return (a.startTime || '').localeCompare(b.startTime || '');
    });
  }, [calendars, events, getEventsForDate]);
  const [notesAccordionOpen, setNotesAccordionOpen] = useState(true);

  // Sıradaki mola geri sayacı
  const [nextBreak, setNextBreak] = useState(null);
  const breakTimerRef = useRef(null);

  useEffect(() => {
    const tick = () => setNextBreak(getNextBreakInfo(breaks));
    tick();
    breakTimerRef.current = setInterval(tick, 1000);
    return () => clearInterval(breakTimerRef.current);
  }, [breaks]);

  // Plus promo modal
  const [showPlusModal, setShowPlusModal] = useState(false);

  // Manage categories modal
  const [showManageCategoriesModal, setShowManageCategoriesModal] = useState(false);

  // Kategoriler accordion — varsayılan kapalı
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  // Active category filter state
  const [activeCategoryFilter, setActiveCategoryFilter] = useState(null);

  // Category context menu
  const [categoryContextMenu, setCategoryContextMenu] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
    category: null,
  });
  const [deleteCategoryModal, setDeleteCategoryModal] = useState({ isOpen: false, category: null });

  // API kategorileri
  const [apiCategories, setApiCategories] = useState([]);

  const fetchApiCategories = useCallback(async () => {
    if (!authToken) return;
    try {
      const data = await categoriesApi.getAll(authToken);
      if (data.ok) setApiCategories(data.categories || []);
    } catch { /* sessizce devam */ }
  }, [authToken]);

  useEffect(() => { fetchApiCategories(); }, [fetchApiCategories]);

  useEffect(() => {
    const handler = () => fetchApiCategories();
    window.addEventListener('notesplus-categories-updated', handler);
    return () => window.removeEventListener('notesplus-categories-updated', handler);
  }, [fetchApiCategories]);

  const sharedNotesCount = React.useMemo(
    () => notes.filter((n) => n.isCollaborative === true).length,
    [notes]
  );

  // Kategoriler: API listesi temel; notlarda olup listede olmayan ekstra kategoriler de dahil
  const allCategories = React.useMemo(() => {
    const catMap = new Map();
    apiCategories.forEach((c) => catMap.set(c.name, c));
    notes.forEach((note) => {
      (note.categories || []).forEach((cat) => {
        if (cat.name && !catMap.has(cat.name)) catMap.set(cat.name, cat);
      });
    });
    return Array.from(catMap.values()).sort((a, b) => a.name.localeCompare(b.name, i18n.language));
  }, [apiCategories, notes]);

  const handleNav = (item) => {
    onSectionChange(item.id);
    navigate(item.path);
  };

  const handleNotesClick = (e) => {
    if (e.target.closest('.notes-chevron')) {
      e.stopPropagation();
      setNotesAccordionOpen(!notesAccordionOpen);
      return;
    }
    onSectionChange('notes');
    navigate('/notes');
  };

  const handleAllNotesClick = () => {
    setActiveCategoryFilter(null);
    onSectionChange('notes');
    navigate('/notes');
    window.dispatchEvent(new CustomEvent('notesplus-filter-clear'));
  };

  const handleCategoryClick = (categoryName) => {
    setActiveCategoryFilter(categoryName);
    onSectionChange('notes');
    navigate('/notes');
    window.dispatchEvent(new CustomEvent('notesplus-filter-category', {
      detail: { category: categoryName }
    }));
  };

  const handleSharedClick = () => {
    if (!isPlusUser) {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: {
          type: 'info',
          title: t('navSidebar.plusRequiredTitle'),
          message: t('navSidebar.plusRequiredMessage'),
        },
      }));
      return;
    }
    setActiveCategoryFilter(null);
    onSectionChange('notes');
    navigate('/notes');
    window.dispatchEvent(new CustomEvent('notesplus-filter-shared'));
  };

  const handleCategoryContextMenu = (e, cat) => {
    e.preventDefault();
    e.stopPropagation();
    setCategoryContextMenu({ isOpen: true, position: { x: e.clientX, y: e.clientY }, category: cat });
  };

  const handleCategoryContextDelete = (cat) => {
    setCategoryContextMenu({ isOpen: false, position: { x: 0, y: 0 }, category: null });
    setDeleteCategoryModal({ isOpen: true, category: cat });
  };

  const handleDeleteCategoryConfirm = async () => {
    const cat = deleteCategoryModal.category;
    setDeleteCategoryModal({ isOpen: false, category: null });
    if (!cat) return;
    try {
      const data = await categoriesApi.remove(authToken, cat.id);
      if (data.ok) {
        updateCategoryInNotes(cat.name, null, null);
        if (activeCategoryFilter === cat.name) setActiveCategoryFilter(null);
        fetchApiCategories();
        window.dispatchEvent(new CustomEvent('notesplus-categories-updated'));
      }
    } catch (err) {
      console.error(t('navSidebar.categoryDeleteError'), err);
    }
  };

  const categoryContextItems = categoryContextMenu.category
    ? [
        {
          label: t('navSidebar.edit'),
          icon: Pencil,
          onClick: () => {
            setCategoryContextMenu({ isOpen: false, position: { x: 0, y: 0 }, category: null });
            setShowManageCategoriesModal(true);
          },
        },
        {
          label: t('navSidebar.delete'),
          icon: Trash2,
          danger: true,
          onClick: () => handleCategoryContextDelete(categoryContextMenu.category),
        },
      ]
    : [];

  const isActive = (item) => {
    return location.pathname === item.path && activeSection === item.id;
  };

  const isNotesActive = () => {
    return location.pathname === '/notes' && activeSection === 'notes';
  };


  // Smooth label geçişi için ortak stil helper'ı
  const labelStyle = {
    opacity: isCollapsed ? 0 : 1,
    maxWidth: isCollapsed ? '0px' : '200px',
    transition: 'opacity 250ms ease-in-out, max-width 300ms ease-in-out',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    display: 'block',
  };

  // Buton padding: collapsed'da ikonu tam ortalar.
  // Container'da yatay padding yok → button w-full = 64px.
  // (64 - 20px ikon) / 2 = 22px her iki yanda
  // NOT: paddingInlineStart/End (paddingLeft/Right DEĞİL) kullanılıyor — bunlar CSS mantıksal
  // özellikleridir, tarayıcı <html dir="rtl"> olduğunda otomatik olarak yön değiştirir.
  // Inline style olduğu için postcss-rtlcss (derlenmiş CSS'i işliyor) bunlara dokunamaz.
  const btnPadding = {
    paddingInlineStart: isCollapsed ? '22px' : '16px',
    paddingInlineEnd: isCollapsed ? '22px' : '12px',
    paddingTop: '10px',
    paddingBottom: '10px',
    transition: 'padding 300ms ease-in-out, background-color 150ms ease-in-out, color 150ms ease-in-out',
  };

  return (
    <aside
      className="bg-slate-50 dark:bg-[var(--color-bg-base)] border-r border-slate-200 dark:border-white/5 flex flex-col justify-between shrink-0 z-40 overflow-hidden"
      style={{ width: isCollapsed ? '64px' : '256px', transition: 'width 300ms ease-in-out' }}
    >
      {/* Brand / Top Nav */}
      <div className="flex flex-col pt-4">
        {/* Logo + Daralt/Genişlet butonu yan yana */}
        <div
          className="flex items-center gap-2 border-b border-slate-200 dark:border-white/5 pb-3 mb-2"
          style={{ paddingInlineStart: '16px' }}
        >
          <button
            onClick={onToggle}
            title={isCollapsed ? t('navSidebar.expandMenu') : t('navSidebar.collapseMenu')}
            className="p-2 rounded-lg text-[var(--color-text-muted)] hover:bg-[#1e2d47] hover:text-[var(--color-text-secondary)] transition-colors flex-shrink-0"
          >
            <ChevronLeft
              className="w-5 h-5"
              style={{ transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 300ms ease-in-out' }}
            />
          </button>
          {!isElectron && (
            <div
              style={{
                opacity: isCollapsed ? 0 : 1,
                maxWidth: isCollapsed ? '0px' : '160px',
                overflow: 'hidden',
                transition: 'opacity 200ms ease-in-out, max-width 300ms ease-in-out',
              }}
            >
              <img
                src={process.env.PUBLIC_URL + "/darkmode.png"}
                alt="Notes+"
                className="h-7 w-auto object-contain"
              />
            </div>
          )}
        </div>
        {/* Nav */}
        <nav className="flex flex-col gap-1">
          {/* Notes Accordion */}
          <div>
            <button
              onClick={handleNotesClick}
              title={isCollapsed ? t('navSidebar.myNotes') : undefined}
              style={btnPadding}
              className={`flex items-center gap-3 rounded-lg font-medium text-left w-full ${
                isNotesActive()
                  ? 'bg-[#1e2d47] text-[var(--color-text-primary)]'
                  : 'text-[var(--color-text-muted)] hover:bg-[#1e2d47] hover:text-[var(--color-text-primary)]'
              }`}
            >
              <FileText className="w-5 h-5 shrink-0" strokeWidth={isNotesActive() ? 2.5 : 2} />
              <span style={{ ...labelStyle, flex: 1 }}>{t('navSidebar.myNotes')}</span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setNotesAccordionOpen(!notesAccordionOpen);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setNotesAccordionOpen(!notesAccordionOpen);
                  }
                }}
                style={labelStyle}
                className="notes-chevron p-0.5 hover:bg-white/10 rounded transition-colors shrink-0"
              >
                {notesAccordionOpen ? (
                  <ChevronDown className="w-4 h-4 text-[var(--color-text-muted)]" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-[var(--color-text-muted)]" />
                )}
              </span>
            </button>

            {/* Sub-menu */}
            <AnimatePresence>
              {notesAccordionOpen && !isCollapsed && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                  className="overflow-hidden"
                >
                  <div className="pl-10 ml-2 border-l border-white/[0.08] mt-1 space-y-1">

                    <button
                      onClick={handleAllNotesClick}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors text-left w-full text-[var(--color-text-muted)] hover:bg-[#1e2d47] hover:text-[var(--color-text-primary)]"
                    >
                      <FileText className="w-4 h-4 shrink-0" strokeWidth={2} />
                      <span className="flex-1 truncate text-sm">{t('navSidebar.allNotes')}</span>
                    </button>

                    <button
                      onClick={handleSharedClick}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors text-left w-full text-[var(--color-text-muted)] hover:bg-[#1e2d47] hover:text-[var(--color-text-primary)]"
                    >
                      <Share2 className="w-4 h-4 shrink-0" strokeWidth={2} />
                      <span className="flex-1 truncate text-sm">{t('navSidebar.collaboration')}</span>
                      {isPlusUser ? (
                        <span className="ml-auto bg-white/10 text-[var(--color-text-primary)] text-xs font-bold px-2 py-0.5 rounded-full">
                          {sharedNotesCount}
                        </span>
                      ) : null}
                    </button>

                    <div className="mt-2">
                      {/* Kategoriler başlık + aç/kapat + düzenle ikonu */}
                      <div className="flex items-center px-3 mb-0.5 group">
                        <button
                          onClick={() => setCategoriesOpen((v) => !v)}
                          className="flex items-center gap-1.5 flex-1 min-w-0 py-1"
                        >
                          <span className="text-[10px] text-[var(--color-text-muted)]/60 font-semibold uppercase tracking-wider">
                            {t('navSidebar.categoriesHeading')}
                          </span>
                          <ChevronRight
                            className="w-3 h-3 text-[var(--color-text-muted)]/50 shrink-0 transition-transform duration-200"
                            style={{ transform: categoriesOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
                          />
                        </button>
                        <button
                          onClick={() => setShowManageCategoriesModal(true)}
                          className="p-1 rounded-md text-[var(--color-text-muted)]/40 hover:text-[var(--color-text-muted)] hover:bg-white/[0.06] transition-colors opacity-0 group-hover:opacity-100"
                          title={t('navSidebar.editCategories')}
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Kategori listesi accordion */}
                      <AnimatePresence initial={false}>
                        {categoriesOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                            className="overflow-hidden"
                          >
                            {allCategories.length === 0 ? (
                              <button
                                onClick={() => setShowManageCategoriesModal(true)}
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-[var(--color-text-muted)]/50 hover:text-[var(--color-text-muted)] hover:bg-white/[0.04] rounded-lg transition-colors"
                              >
                                <Plus className="w-3 h-3 flex-shrink-0" />
                                <span style={labelStyle}>{t('navSidebar.addCategory')}</span>
                              </button>
                            ) : (
                              <div className="space-y-0.5 pb-1">
                                {allCategories.map((category) => {
                                  const catName = category.name;
                                  const catColor = category.color || '#3b82f6';
                                  const CatIcon = getCategoryIcon(category.icon);
                                  const isActive = activeCategoryFilter === catName;
                                  return (
                                    <button
                                      key={catName}
                                      onClick={() => handleCategoryClick(catName)}
                                      onContextMenu={(e) => handleCategoryContextMenu(e, category)}
                                      style={btnPadding}
                                      className={`flex items-center gap-2.5 rounded-lg font-medium transition-colors text-left w-full ${
                                        isActive
                                          ? 'bg-[#1e2d47] text-[var(--color-text-primary)]'
                                          : 'text-[var(--color-text-muted)] hover:bg-[#1e2d47] hover:text-[var(--color-text-primary)]'
                                      }`}
                                    >
                                      <div
                                        className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0"
                                        style={{ backgroundColor: catColor + '22' }}
                                      >
                                        <CatIcon className="w-3 h-3" style={{ color: catColor }} />
                                      </div>
                                      <span style={labelStyle} className="flex-1 truncate text-sm">{catName}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Other Nav Items */}
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            const showChatDot = item.id === 'chat' && hasUnreadMessages && !active;

            return (
              <button
                key={item.id}
                onClick={() => handleNav(item)}
                title={isCollapsed ? item.label : undefined}
                style={btnPadding}
                className={`relative flex items-center gap-3 rounded-lg font-medium text-left w-full ${
                  active
                    ? 'bg-[#1e2d47] text-[var(--color-text-primary)]'
                    : 'text-[var(--color-text-muted)] hover:bg-[#1e2d47] hover:text-[var(--color-text-primary)]'
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" strokeWidth={active ? 2.5 : 2} />
                <span style={labelStyle}>{item.label}</span>
                {showChatDot && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" aria-hidden />
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Alt alan: Bugünkü Etkinlikler + Sıradaki Mola + Daralt butonu */}
      <div className="border-t border-slate-200 dark:border-white/5 flex flex-col">
        {/* Bugünkü Etkinlikler */}
        {todayEvents.length > 0 && (
          <div
            className="overflow-hidden border-b border-white/5"
            style={{
              padding: isCollapsed ? '10px 0' : '10px 12px',
              transition: 'padding 300ms ease-in-out',
            }}
          >
            {isCollapsed ? (
              <div
                className="flex justify-center relative"
                title={t('navSidebar.todayEventsTooltip', { count: todayEvents.length })}
              >
                <Calendar className="w-4 h-4 text-[#3d8ef0]" />
                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#3d8ef0] text-white text-[8px] font-bold flex items-center justify-center leading-none">
                  {todayEvents.length}
                </span>
              </div>
            ) : (
              <div className="flex flex-col gap-1 px-1">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Calendar className="w-3.5 h-3.5 text-[#3d8ef0] shrink-0" />
                  <span className="text-[10px] font-medium text-[var(--color-text-muted)] uppercase tracking-wide whitespace-nowrap">{t('navSidebar.today')}</span>
                </div>
                {todayEvents.slice(0, 3).map((event) => (
                  <div key={event.id} className="flex items-center gap-1.5 pl-5">
                    <span
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ backgroundColor: event.calendar?.color || '#3d8ef0' }}
                    />
                    <span className="text-[11px] text-[var(--color-text-secondary)] truncate leading-4">
                      {event.allDay ? '' : event.startTime ? `${event.startTime.slice(0, 5)} ` : ''}{event.title}
                    </span>
                  </div>
                ))}
                {todayEvents.length > 3 && (
                  <span className="text-[10px] text-[var(--color-text-muted)] pl-5">{t('navSidebar.moreEvents', { count: todayEvents.length - 3 })}</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Plus CTA — yalnızca Plus olmayan kullanıcılara */}
        {!isPlusUser && (
          <div
            className="overflow-hidden border-b border-white/5"
            style={{
              padding: isCollapsed ? '10px 0' : '10px 12px',
              transition: 'padding 300ms ease-in-out',
            }}
          >
            {isCollapsed ? (
              <button
                type="button"
                onClick={() => setShowPlusModal(true)}
                title={t('navSidebar.upgradeToPlus')}
                className="mx-auto flex h-9 w-9 items-center justify-center rounded-xl border border-white/[0.08] bg-[#161f31] text-amber-400 transition-all hover:border-amber-500/25 hover:bg-[#1a2640]"
              >
                <Crown className="h-4 w-4" />
              </button>
            ) : (
              <div className="rounded-2xl border border-white/[0.08] bg-[#161f31] p-3.5">
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-500/15">
                    <Crown className="h-3.5 w-3.5 text-amber-400" />
                  </div>
                  <h4 className="text-sm font-bold text-amber-400">{t('navSidebar.upgradeToPlusTitle')}</h4>
                </div>
                <p className="mt-2 text-[11px] leading-relaxed text-[#7a96b0]">
                  {t('navSidebar.plusPromoDescription')}
                </p>
                <button
                  type="button"
                  onClick={() => setShowPlusModal(true)}
                  className="mt-3 w-full rounded-full border border-amber-400/30 bg-amber-500/20 py-2 text-[11px] font-bold uppercase tracking-wider text-amber-300 transition-all hover:border-amber-400/40 hover:bg-amber-500/30 active:scale-[0.98]"
                >
                  {t('navSidebar.upgradeNow')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Sıradaki Mola Geri Sayacı */}
        {nextBreak && (
          <div
            className="overflow-hidden"
            style={{
              padding: isCollapsed ? '10px 0' : '10px 12px',
              transition: 'padding 300ms ease-in-out',
            }}
          >
            {isCollapsed ? (
              <div className="flex justify-center" title={t('navSidebar.nextBreakTooltip', { name: nextBreak.name, time: formatCountdown(nextBreak.secondsUntil) })}>
                <Timer className="w-4 h-4 text-[#3d8ef0]" />
              </div>
            ) : (
              <div className="flex flex-col gap-0.5 px-1">
                <div className="flex items-center gap-1.5">
                  <Timer className="w-3.5 h-3.5 text-[#3d8ef0] shrink-0" />
                  <span className="text-[10px] font-medium text-[var(--color-text-muted)] uppercase tracking-wide whitespace-nowrap">{t('navSidebar.nextBreak')}</span>
                </div>
                <div className="flex items-baseline gap-1.5 pl-5">
                  <span
                    className="text-xs font-semibold text-[var(--color-text-secondary)] truncate max-w-[80px]"
                    title={nextBreak.name}
                  >
                    {nextBreak.name}
                  </span>
                  <span className="text-xs font-bold text-[#3d8ef0] tabular-nums whitespace-nowrap">
                    {formatCountdown(nextBreak.secondsUntil)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Plus Promo Modal */}
      <PlusPromoModal isOpen={showPlusModal} onClose={() => setShowPlusModal(false)} />

      {/* Manage Categories Modal */}
      <ManageCategoriesModal
        isOpen={showManageCategoriesModal}
        onClose={() => setShowManageCategoriesModal(false)}
      />

      {/* Category Context Menu */}
      <ContextMenu
        isOpen={categoryContextMenu.isOpen}
        position={categoryContextMenu.position}
        items={categoryContextItems}
        onClose={() => setCategoryContextMenu({ isOpen: false, position: { x: 0, y: 0 }, category: null })}
      />

      {/* Delete Category Confirm Modal */}
      <DeleteModal
        isOpen={deleteCategoryModal.isOpen}
        onClose={() => setDeleteCategoryModal({ isOpen: false, category: null })}
        onConfirm={handleDeleteCategoryConfirm}
        title={t('navSidebar.deleteCategoryTitle')}
        message={t('navSidebar.deleteCategoryMessage', { name: deleteCategoryModal.category?.name })}
      />
    </aside>
  );
};

export default NavSidebar;
